import React, { memo, useCallback, useEffect } from 'react';

import {
  DefaultCustomRatePayload,
  ProductsRateResponse
} from '../../../store/_declarations/financialCalculator';
import { AsyncStatus } from '../../../store/_declarations/globals';
import {
  postDefaultCustomRate,
  postProductsRate
} from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import Spinner from '../../atoms/Spinner';
import WidgetWrapper from '../../atoms/WidgetWrapper';
import ProductCard from '../../organisms/ProductCard';
import { ProductListWrapper, SpinnerContainer } from './styles';

type StatusFC = {
  postDefaultCustomRate?: AsyncStatus;
  postProductsRate?: AsyncStatus;
};

const useButtonStates = (
  statusFC?: StatusFC,
  statusAuth?: AsyncStatus,
  isFirstLoad?: boolean
) => {
  const isStatusAuthLoading = statusAuth === 'loading';

  const buttonDisabledCond1 = isFirstLoad && isStatusAuthLoading;

  const buttonConfirmDisabled =
    statusFC?.postProductsRate === 'loading' ||
    statusAuth === 'failed' ||
    statusFC?.postProductsRate === 'failed' ||
    statusFC?.postDefaultCustomRate === 'loading' ||
    statusFC?.postDefaultCustomRate === 'failed' ||
    buttonDisabledCond1;

  return { buttonConfirmDisabled };
};

const useModalButtonVisibility = (
  statusFC?: StatusFC,
  statusAuth?: AsyncStatus,
  isFirstLoad?: boolean,
  widgetType?: string
) => {
  const isWidgetTypeModal = widgetType === 'modal';

  const buttonShowOnModalCond1 = statusAuth !== 'success' && isFirstLoad;
  const buttonShowOnModalCond2 = isFirstLoad && statusAuth === 'loading';

  const buttonShowOnModal =
    buttonShowOnModalCond1 ||
    statusFC?.postProductsRate !== 'success' ||
    buttonShowOnModalCond2;

  const buttonConfirmHidden = !isWidgetTypeModal ? false : buttonShowOnModal;

  return { buttonConfirmHidden };
};

const SpinnerComponent = () => (
  <SpinnerContainer data-testid="spinner-main-view">
    <Spinner size={52} />
  </SpinnerContainer>
);

type ListComponentProps = {
  productsRate: ProductsRateResponse;
  selectedItem?: string;
  statusFC?: { postDefaultCustomRate?: string };
  widgetType?: string;
};

const ListComponent = ({
  productsRate,
  selectedItem,
  statusFC,
  widgetType
}: ListComponentProps) => (
  <ProductListWrapper $widgetType={widgetType}>
    {productsRate.packageSelection.map((item) => (
      <ProductCard
        isLoading={
          statusFC?.postDefaultCustomRate === 'loading' &&
          item.uuid === selectedItem
        }
        item={item}
        key={item.uuid}
        selected={item.uuid === selectedItem}
      />
    ))}
  </ProductListWrapper>
);

const MainView: React.FC<{
  closeModal: () => void;
  outputFn?: (output: any) => void;
}> = memo(({ closeModal, outputFn }) => {
  const { consumerPayload, selectedItem, updateWidget, widgetType } =
    useAppSelector((state) => state.app);
  const { status: statusAuth, value } = useAppSelector((state) => state.auth);
  const { productsRate, status: statusFC } = useAppSelector(
    (state) => state.financialCalculator
  );
  const dispatch = useAppDispatch();
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);
  const firstLoad = statusFC?.postProductsRate === 'loading' || !value;

  const { buttonConfirmDisabled } = useButtonStates(
    statusFC,
    statusAuth,
    firstLoad
  );
  const { buttonConfirmHidden } = useModalButtonVisibility(
    statusFC,
    statusAuth,
    firstLoad,
    widgetType
  );

  useEffect(() => {
    if (consumerPayload && statusAuth === 'success' && isFirstLoad) {
      const pprPayload: DefaultCustomRatePayload = {
        ...consumerPayload
      };

      dispatch(postProductsRate(pprPayload));

      setIsFirstLoad(false);
    }
  }, [statusAuth, consumerPayload]);

  useEffect(() => {
    if (consumerPayload && updateWidget) {
      const pprPayload: DefaultCustomRatePayload = {
        ...consumerPayload
      };

      dispatch(postProductsRate(pprPayload));
    }
  }, [updateWidget]);

  useEffect(() => {
    if (
      statusFC.postProductsRate === 'success' &&
      consumerPayload?.parameters
    ) {
      const pdcprPayload: DefaultCustomRatePayload = {
        ...consumerPayload
      };

      dispatch(postDefaultCustomRate({ ...pdcprPayload, uuid: selectedItem }));
    }
  }, [statusFC.postProductsRate]);

  const confirmModal = useCallback(() => {
    if (outputFn) {
      const item = productsRate?.packageSelection.find(
        (i: ProductsRateResponse.PackageSelection) => i.uuid === selectedItem
      );
      const cleanedItem = { ...item };
      delete cleanedItem.criteriaAdjustment;
      delete cleanedItem.uuid;
      outputFn(cleanedItem);
    }
    closeModal();
  }, [closeModal, selectedItem, productsRate]);

  const showList = () => {
    if (firstLoad || !productsRate) {
      return <SpinnerComponent />;
    }

    return (
      <ListComponent
        productsRate={productsRate}
        selectedItem={selectedItem}
        statusFC={statusFC}
        widgetType={widgetType}
      />
    );
  };

  return (
    <WidgetWrapper
      disableConfirmButton={buttonConfirmDisabled}
      hideConfirmButton={buttonConfirmHidden}
      onClickClose={closeModal}
      onClickConfirm={confirmModal}
    >
      {showList()}
    </WidgetWrapper>
  );
});
export default MainView;
